/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  ActivityIndicator,
  Platform,
  Dimensions,
  TouchableOpacity,
} from "react-native";
import DLOBreadcrumb, { DLOBreadcrumbItem } from "../../../modules/dlo/components/DLOBreadcrumb";
import EmployeeProfileForm from "../components/EmployeeProfile/EmployeeProfileForm";
import FontFamily from "../../../constants/fonts";
import ProbationaryReviewSection, {
  ProbationReviewData,
} from "../components/EmployeeProfile/ProbationaryReviewSection";
import LhButton from "../../../components/LhButton";
import EmployeeMetricTabs from "../components/EmployeeProfile/EmployeeMetricTabs";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { useToast } from "../../../components/LhToastContainer";
import TrainingOverlay from "../components/TrainingSignoff/TrainingOverlay";
import { DynamicTrainingContent } from "../components/TrainingSignoff/TrainingContents";
import {
  fetchTrainingContents,
  fetchUndertakingDetails,
} from "../../../store/slices/employeeProfileSlice";
import { useAppDispatch } from "../../../store";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  setEmployeeDataFromApi,
  resetEmployeeData,
} from "../../../store/slices/employeeUpdatesSlice";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../store/utils/nhoErrorHandler';
import { useTheme } from "../../../theme";

// Define the training type mapping to make it easier to determine completed trainings
const TRAINING_TYPE_MAP = {
  "Safety Training Sign Off": 1,
  "PIT Training": 2,
  "VP Equipment": 3,
  "Metrics Explained": 4,
  "Performance Expectation": 5,
  "Management Commitment": 6,
};

// Define props for EmployeeProfile component
interface EmployeeProfileProps {
  employeeId?: string;
  onBackPress?: () => void;
  onHomePress?: () => void; // Added for home navigation
}

// Define interface for employee profile details
interface EmployeeDetails {
  name: string;
  peerCoach: string;
  gpid: string;
  Status: string;
  Job_Description: string;
  Manager: string;
  graduationDate: Date | null;
  hiringDate: Date | null;
  level?: string;
  slotId?: string;
  New_Hire?: number; // Default to 0 for new hires
}

// Define tab type
type Tab = {
  id: string;
  title: string;
  icon: string;
};

const EmployeeProfile: React.FC<EmployeeProfileProps> = ({
  employeeId = "No ID provided",
  onBackPress,
  onHomePress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Initialize toast hook
  const toast = useToast();
  const dispatch = useAppDispatch();

  // Get undertakings data from Redux store
  const { undertakings } = useSelector(
    (state: RootState) => state.employeeProfile
  );
  // Get selected location from Redux store
  const { selectedLocation } = useSelector((state: RootState) => state.appData);
  const [probationData, setProbationData] = useState<ProbationReviewData>({
    "Preferred Method": null,
    "Probation 30": null,
    "Probation 60": null,
    "Probation 90": null,
    "Milestone 1": null,
    "Milestone 2": null,
    "Milestone 3": null,
  });

  const [employeeDetails, setEmployeeDetails] = useState<EmployeeDetails>({
    name: "",
    peerCoach: "",
    gpid: "",
    Status: "",
    Job_Description: "",
    Manager: "",
    graduationDate: null,
    hiringDate: null,
    New_Hire: 0, // Default to 0 for new hires
  });

  const [isFormModified, setIsFormModified] = useState<boolean>(false);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Use this local state to track API call status in component
  const [isUndertakingLoading, setIsUndertakingLoading] =
    useState<boolean>(false);

  // Training overlay states
  const [trainingOverlayVisible, setTrainingOverlayVisible] = useState(false);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [trainingContent, setTrainingContent] = useState<React.ReactNode>(null);

  // Define tabs for the employee profile
  const tabs: Tab[] = [
    { id: "dps", title: "DPS", icon: "person" },
    { id: "cph", title: "CPH", icon: "psychology" },
    { id: "gap-time", title: "Gap Time", icon: "description" },
    { id: "sia", title: "SIA", icon: "school" },
    { id: "attendance", title: "Attendance", icon: "rate-review" },
  ];

  // State to track the active tab
  const [activeTab, setActiveTab] = useState<string>(tabs[0].id);

  // Utility to chunk an array into groups
  const chunkArray = (arr: any[], size: number) =>
    arr.reduce((acc, _, i) =>
      i % size === 0 ? [...acc, arr.slice(i, i + size)] : acc, []);

  const trainingButtons = [
    { title: "Safety Training Sign Off", contentId: "safety-training" },
    { title: "PIT Training", contentId: "pit-training" },
    { title: "VP Equipment", contentId: "vp-equipment" },
    { title: "Metrics Explained", contentId: "metrics-explained" },
    { title: "Performance Expectation", contentId: "performance-expectations" },
    { title: "Management Commitment", contentId: "management-commitment" },
  ];

  // Fetch employee data when component mounts or employeeId changes
  useEffect(() => {
    // Skip the API call if there's no valid employeeId
    if (!employeeId || employeeId === "No ID provided") {
      setError("No valid employee ID provided");
      return;
    }

    // Fetch training contents
    dispatch(fetchTrainingContents(employeeId));

    const fetchEmployeeData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Create API client
        const api = createApiClient();

        // Call the API to fetch employee data
        const response = await apiClient(
          api,
          `/nho/employees/${employeeId}`,
          "GET"
        );

        console.log("API Response:", response);

        if (response) {
          // Map API response to employee details
          setEmployeeDetails({
            name:
              response.Full_Name ||
              `${response.First_Name || ""} ${response.Last_Name || ""}`.trim(),
            peerCoach: response.Peer_Coach || "",
            gpid: response.GPID || "",
            Status: response.Status || "",
            Job_Description: response.Job_Description || "",
            Manager: response.Manager || "",
            graduationDate: response.Expected_Graduation_Date
              ? new Date(response.Expected_Graduation_Date)
              : null,
            hiringDate: response.Hire_Date
              ? new Date(response.Hire_Date)
              : response.Joining_Date
              ? new Date(response.Joining_Date)
              : null,
            New_Hire: response.New_Hire || 0, // Default to 0 for new hires
          });

          // Dispatch action to update the Redux store with employee data
          dispatch(
            setEmployeeDataFromApi({
              firstName: response.First_Name || "",
              lastName: response.Last_Name || "",
              gpid: response.GPID || "",
              jobDescription: response.Job_Description || "",
              manager: response.Manager || "",
            })
          );

          console.log("Employee Details:", response);
          console.log("Mapped Employee Details:", setEmployeeDetails);

          // Get ID from response and use it to fetch undertaking details
          const employeeId = response.ID;

          if (employeeId) {
            console.log("Employee ID found:", employeeId);
            setIsUndertakingLoading(true);

            try {
              // Dispatch the fetchUndertakingDetails thunk with the employee ID
              const resultAction = await dispatch(
                fetchUndertakingDetails(employeeId)
              );

              if (fetchUndertakingDetails.fulfilled.match(resultAction)) {
                console.log(
                  "Undertaking details fetched successfully:",
                  resultAction.payload
                );
              } else if (fetchUndertakingDetails.rejected.match(resultAction)) {
                console.error(
                  "Error fetching undertaking details:",
                  resultAction.payload
                );
              }
            } catch (undertakingError) {
              console.error(
                "Exception while fetching undertaking details:",
                undertakingError
              );
            } finally {
              setIsUndertakingLoading(false);
            }
          } else {
            console.warn("No employee ID found in response");
          }

          // Reset form modification state as we've just loaded fresh data
          setIsFormModified(false);
        }
      } catch (err) {
        console.error("Error fetching employee data:", err);
        try {
          handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_NHO_BY_ID, err, true, { employeeId });
        } catch (e) {
          console.error('Error while dispatching NHO centralized handler for nho employee by id:', e);
        }
        setError("Failed to load employee details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployeeData();
  }, [employeeId, dispatch]); // Re-run when employeeId changes

  const handleEmployeeDetailChange = (
    field: keyof EmployeeDetails,
    value: string | Date | null
  ) => {
    // Prevent modification of name field
    if (field === "name") {
      return;
    }

    if (
      field === "peerCoach" ||
      field === "graduationDate" ||
      field === "hiringDate"
    ) {
      setIsFormModified(true);
    }

    setEmployeeDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Function to check if a training is completed
  const isTrainingCompleted = (trainingTitle: string) => {
    // Find the undertaking item that matches the training title by looking up its ID
    const trainingTypeId =
      TRAINING_TYPE_MAP[trainingTitle as keyof typeof TRAINING_TYPE_MAP];

    // Find the undertaking that matches the training type ID
    const undertaking = undertakings.data?.find(
      (item) => item.Training_type === trainingTypeId
    );

    // If undertaking exists, check the employee sign status
    return undertaking?.Emp_Sign_Status === 1;
  };

  const handleSave = async () => {
    // Skip saving if the form is not modified
    if (!isFormModified) {
      toast.showToast({
        header: "No Changes",
        subtext: "Please modify the form before saving.",
        variant: "warning",
      });
      return;
    }

    // Add your save logic here, e.g., API call to save employee details
    setIsSaving(true);
    setError(null);

    try {
      // Create API client
      const api = createApiClient();

      // Extract first and last name from the full name
      const nameParts = employeeDetails.name.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      // Use local date parts to avoid UTC day-shift from toISOString
      const toLocalDateString = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      // Prepare data for saving according to the PUT schema
      const dataToSave = {
        First_Name: firstName,
        Last_Name: lastName,
        GPID: employeeDetails.gpid || "",
        Status: employeeDetails.Status || "",
        Job_Description: employeeDetails.Job_Description || "",
        Manager: employeeDetails.Manager || "",
        Peer_Coach: employeeDetails.peerCoach || "",
        Expected_Graduation_Date: employeeDetails.graduationDate
          ? toLocalDateString(employeeDetails.graduationDate)
          : null,
        Hire_Date: employeeDetails.hiringDate
          ? toLocalDateString(employeeDetails.hiringDate)
          : null,
        New_Hire: employeeDetails.New_Hire || 0,
        location: selectedLocation?.displayText || "",
      };
      console.log("Data to save:", dataToSave);
      // Call the API to save employee data
      const response = await apiClient(
        api,
        `/nho/employees/${employeeId}`,
        "PUT",
        dataToSave
      );

      console.log("Save response:", response);

      // Check if response exists - API might not return a specific success field
      if (response) {
        // Show toast notification for success
        toast.showToast({
          header: "Success",
          subtext: "Employee details saved successfully.",
          variant: "success",
          position: "top",
        });

        // Reset form modification state after successful save
        setIsFormModified(false);

        // Optionally, you can refetch the employee data to get the latest details
        // fetchEmployeeData();
      } else {
        throw new Error("Failed to save employee details");
      }
    } catch (err) {
      console.error("Error saving employee data:", err);
      setError("Failed to save employee details");

      // Show toast notification for error
      toast.showToast({
        header: "Error",
        subtext: "Failed to save employee details. Please try again later.",
        variant: "error",
        position: "top",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add function to fetch probation data
  const fetchProbationData = async () => {
    console.log("fetchProbationData called with:", {
      gpid: employeeDetails.gpid,
      location: selectedLocation?.displayText,
    });

    if (!employeeDetails.gpid) {
      return;
    }

    // Use selected location from Redux store appData -> selectedLocation -> displayText
    if (!selectedLocation?.displayText) {
      console.warn("No location selected in Redux store");
      return;
    }

    try {
      const api = createApiClient();
      const location = selectedLocation.displayText; // Use location from Redux store only
      const url = `/new-hire-onboarding/probation/summary/${
        employeeDetails.gpid
      }?location=${encodeURIComponent(location)}`;
      console.log("Fetching probation data from:", url);
      console.log("Using location from Redux store:", location);
      const response = await apiClient(api, url, "GET");
      setProbationData(response);
      console.log("Probation data fetch completed successfully");
    } catch (error) {
      console.error("Error fetching probation data:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_PROBATION_SUMMARY, error, true, { gpid: employeeDetails.gpid, location: selectedLocation?.displayText });
      } catch (e) {
        console.error('Error while dispatching NHO centralized handler for fetchProbationData:', e);
      }
    }
  };

  // Fetch probation data when employee details change or when location changes
  // Use a ref to track if we've already fetched data for this combination
  const lastFetchedRef = useRef<string>("");

  useEffect(() => {
    console.log("Probation useEffect triggered:", {
      gpid: employeeDetails.gpid,
      location: selectedLocation?.displayText,
      lastFetched: lastFetchedRef.current,
    });

    if (employeeDetails.gpid && selectedLocation?.displayText) {
      // Create a unique key for this combination
      const fetchKey = `${employeeDetails.gpid}-${selectedLocation.displayText}`;

      // Only fetch if we haven't already fetched for this exact combination
      if (lastFetchedRef.current !== fetchKey) {
        console.log("Making probation API call for new combination:", fetchKey);
        lastFetchedRef.current = fetchKey;
        fetchProbationData();
      } else {
        console.log("Skipping duplicate probation API call for:", fetchKey);
      }
    }
  }, [employeeDetails.gpid, selectedLocation?.displayText]);

  const isIOS = Platform.OS === "ios";
  const isIPad = isIOS && Dimensions.get("window").width > 768;
  const isMobile = (Platform.OS === "ios" || Platform.OS === "android") && Dimensions.get("window").width < 600;

  // Breadcrumb items for Employee Profile
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: 'nho',
      label: 'NHO',
      onPress: () => {
        dispatch(resetEmployeeData());
        if (onBackPress) {
          onBackPress();
        }
      },
    },
    {
      id: 'employee-profile',
      label: employeeDetails.name || "Employee Profile",
      isCurrentPage: true,
    },
  ];

  const handleBackPress = () => {
    dispatch(resetEmployeeData());
    if (onBackPress) {
      onBackPress();
    }
  };

  return (
    <ScrollView
      style={[
        styles.container,
        isDarkTheme && { backgroundColor: colors.background.page },
      ]}
    >
      {/* Header with Breadcrumb and Title */}
      <View style={styles.headerContainer}>
        {isMobile ? (
          // Mobile layout: Back button + Title
          <View style={styles.desktopHeader}>
            <DLOBreadcrumb
              items={breadcrumbItems}
              showHomeIcon={true}
              onHomePress={onHomePress}
              style={{ marginBottom: 8 }}
            />
            <Text style={[styles.title, isDarkTheme && { color: colors.text.primary }]}>{employeeDetails.name || 'Employee Profile'}</Text>
          </View>
        ) : (
          // Desktop/Tablet layout: Breadcrumb + Title
          <View style={styles.desktopHeader}>
            <DLOBreadcrumb
              items={breadcrumbItems}
              showHomeIcon={true}
              onHomePress={onHomePress}
              style={{ marginBottom: 8 }}
            />
            <Text style={[styles.title, isDarkTheme && { color: colors.text.primary }]}>{employeeDetails.name || 'Employee Profile'}</Text>
          </View>
        )}
      </View>
      {/* Show loader while fetching employee data */}
      {isLoading && (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color={isDarkTheme ? colors.text.primary : "#002366"} />
          <Text style={[styles.loaderText, isDarkTheme && { color: colors.text.primary }]}>Loading employee data...</Text>
        </View>
      )}

      {/* Only show the form and content when not loading */}
      {!isLoading && (
        <>
          <EmployeeProfileForm
            employeeDetails={employeeDetails}
            handleEmployeeDetailChange={handleEmployeeDetailChange}
            isFormModified={isFormModified}
            onSave={handleSave}
            isSaving={isSaving}
          />

          <View
            style={[
              styles.trainingButtonsContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderBottomColor: colors.border.default,
                borderTopColor: colors.border.default,
              },
            ]}
          >
            {isIOS ? (
              // Responsive iOS layout
              (() => {
                const isIPad = Dimensions.get("window").width > 768;
                const perRow = isIPad ? 3 : 2;
                return chunkArray(trainingButtons, perRow).map((row: any[], rowIdx: React.Key | null | undefined) => (
                  <View style={styles.iosButtonRow} key={rowIdx}>
                    {row.map((btn) => (
                      <LhButton
                        key={btn.title}
                        style={{
                          ...styles.trainingButton,
                          ...(isTrainingCompleted(btn.title)
                            ? {
                                ...styles.completedTrainingButton,
                                backgroundColor: colors.brand.primary,
                              }
                            : isDarkTheme
                            ? {
                                backgroundColor: colors.background.elevated,
                                borderColor: colors.border.default,
                                borderWidth: 1,
                              }
                            : {}),
                        }}
                        textStyle={
                          isTrainingCompleted(btn.title)
                            ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                            : styles.trainingButtonText
                        }
                        text={btn.title}
                        variant="secondary"
                        size="medium"
                        onPress={() => {
                          setTrainingTitle(btn.title);
                          setTrainingContent(
                            <DynamicTrainingContent contentId={btn.contentId} />
                          );
                          setTrainingOverlayVisible(true);
                        }}
                      />
                    ))}
                  </View>
                ));
              })()
            ) : (
              // Web layout - original layout
              <>
                <LhButton
                  style={{
                    ...styles.trainingButton,
                    ...(isTrainingCompleted("Safety Training Sign Off")
                      ? {
                          ...styles.completedTrainingButton,
                          backgroundColor: colors.brand.primary,
                        }
                      : isDarkTheme
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                          borderWidth: 1,
                        }
                      : {}),
                  }}
                  textStyle={
                    isTrainingCompleted("Safety Training Sign Off")
                      ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                      : styles.trainingButtonText
                  }
                  text="Safety Training Sign Off"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setTrainingTitle("Safety Training Sign Off");
                    setTrainingContent(
                      <DynamicTrainingContent contentId="safety-training" />
                    );
                    setTrainingOverlayVisible(true);
                  }}
                />
                <LhButton
                  style={{
                    ...styles.trainingButton,
                    ...(isTrainingCompleted("PIT Training")
                      ? {
                          ...styles.completedTrainingButton,
                          backgroundColor: colors.brand.primary,
                        }
                      : isDarkTheme
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                          borderWidth: 1,
                        }
                      : {}),
                  }}
                  textStyle={
                    isTrainingCompleted("PIT Training")
                      ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                      : styles.trainingButtonText
                  }
                  text="PIT Training"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setTrainingTitle("PIT Training");
                    setTrainingContent(
                      <DynamicTrainingContent contentId="pit-training" />
                    );
                    setTrainingOverlayVisible(true);
                  }}
                />
                <LhButton
                  style={{
                    ...styles.trainingButton,
                    ...(isTrainingCompleted("VP Equipment")
                      ? {
                          ...styles.completedTrainingButton,
                          backgroundColor: colors.brand.primary,
                        }
                      : isDarkTheme
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                          borderWidth: 1,
                        }
                      : {}),
                  }}
                  textStyle={
                    isTrainingCompleted("VP Equipment")
                      ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                      : styles.trainingButtonText
                  }
                  text="VP Equipment"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setTrainingTitle("VP Equipment");
                    setTrainingContent(
                      <DynamicTrainingContent contentId="vp-equipment" />
                    );
                    setTrainingOverlayVisible(true);
                  }}
                />
                <LhButton
                  style={{
                    ...styles.trainingButton,
                    ...(isTrainingCompleted("Metrics Explained")
                      ? {
                          ...styles.completedTrainingButton,
                          backgroundColor: colors.brand.primary,
                        }
                      : isDarkTheme
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                          borderWidth: 1,
                        }
                      : {}),
                  }}
                  textStyle={
                    isTrainingCompleted("Metrics Explained")
                      ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                      : styles.trainingButtonText
                  }
                  text="Metrics Explained"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setTrainingTitle("Metrics Explained");
                    setTrainingContent(
                      <DynamicTrainingContent contentId="metrics-explained" />
                    );
                    setTrainingOverlayVisible(true);
                  }}
                />
                <LhButton
                  style={{
                    ...styles.trainingButton,
                    ...(isTrainingCompleted("Performance Expectation")
                      ? {
                          ...styles.completedTrainingButton,
                          backgroundColor: colors.brand.primary,
                        }
                      : isDarkTheme
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                          borderWidth: 1,
                        }
                      : {}),
                  }}
                  textStyle={
                    isTrainingCompleted("Performance Expectation")
                      ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                      : styles.trainingButtonText
                  }
                  text="Performance Expectation"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setTrainingTitle("Performance Expectation");
                    setTrainingContent(
                      <DynamicTrainingContent contentId="performance-expectations" />
                    );
                    setTrainingOverlayVisible(true);
                  }}
                />
                <LhButton
                  style={{
                    ...styles.trainingButton1,
                    ...(isTrainingCompleted("Management Commitment")
                      ? {
                          ...styles.completedTrainingButton,
                          backgroundColor: colors.brand.primary,
                        }
                      : isDarkTheme
                      ? {
                          backgroundColor: colors.background.elevated,
                          borderColor: colors.border.default,
                          borderWidth: 1,
                        }
                      : {})
                  }}
                  textStyle={
                    isTrainingCompleted("Management Commitment")
                      ? { ...styles.trainingButtonText, ...styles.completedTrainingButtonText }
                      : styles.trainingButtonText
                  }
                  text="Management Commitment"
                  variant="secondary"
                  size="medium"
                  onPress={() => {
                    setTrainingTitle("Management Commitment");
                    setTrainingContent(
                      <DynamicTrainingContent contentId="management-commitment" />
                    );
                    setTrainingOverlayVisible(true);
                  }}
                />
              </>
            )}
          </View>

          {/* Using the EmployeeTabs component */}
          <EmployeeMetricTabs
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            employeeId={employeeDetails.gpid}
          />
        </>
      )}

      {/* Training Overlay */}
      {trainingOverlayVisible && (
        <TrainingOverlay
          visible={trainingOverlayVisible}
          onDismiss={() => setTrainingOverlayVisible(false)}
          title={trainingTitle}
          content={trainingContent}
          employeeId={employeeId !== "No ID provided" ? employeeId : undefined}
          contentId={
            trainingTitle === "Management Commitment"
              ? "management-commitment"
              : undefined
          }
          onTrainingComplete={(completedTitle) => {
            // Refresh undertakings data to update UI
            dispatch(fetchUndertakingDetails());
            // Show success message
            toast.showToast({
              header: "Training Completed",
              subtext: `${completedTitle} has been completed successfully.`,
              variant: "success",
              position: "top",
            });
          }}
        />
      )}

      <ProbationaryReviewSection probationData={probationData} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding:20
  },
  trainingButtonsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: Platform.OS === "ios" ? "flex-start" : "flex-start",
    gap: 10,
    paddingHorizontal: Platform.OS === "ios" ? 20 : 20,
    marginVertical: 0,
    backgroundColor: "white",
    //marginLeft: 20,
    //marginRight: 20,
    paddingBottom: 25,
    paddingTop: 25,
    borderBottomWidth: 1,
    borderBottomColor: "#DADADA",
    borderTopWidth: 1,
    borderTopColor: "#DADADA",
    ...(Platform.OS === "ios" && {
      paddingVertical: 10,
      borderRadius: 0,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 1,
    }),
  },
  iosButtonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: 0,
    paddingHorizontal: 5,
  },
  trainingButton: {
    flex: Platform.OS === "ios" ? 0 : 1,
    minWidth: 150,
    maxWidth:
      Platform.OS === "ios"
        ? Dimensions.get("window").width > 768
          ? "30%"
          : "30%" // 3 buttons per row on iOS
        : "14%", // Approximately 1/6 of the container width for 6 buttons on web
    gap:Platform.OS === "ios" ? 0 : 10,
    backgroundColor: "white",
    ...(Platform.OS === "ios" && {
      width: "30%", 
      marginVertical: 0,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 24,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0,
      shadowRadius: 1,
    }),
  },
  trainingButton1: {
    flex: Platform.OS === "ios" ? 0 : 1,
    minWidth: 150,
    maxWidth:
      Platform.OS === "ios"
        ? Dimensions.get("window").width > 768
          ? "30%"
          : "30%" // 3 buttons per row on iOS
        : "16%", // Approximately 1/6 of the container width for 6 buttons on web
    gap:Platform.OS === "ios" ? 0 : 10,
    backgroundColor: "white",
    ...(Platform.OS === "ios" && {
      width: "30%", 
      marginVertical: 0,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: 24,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0,
      shadowRadius: 1,
    }),
  },
  completedTrainingButton: {
    backgroundColor: "#034895", // Dark blue background for completed training
    //borderColor: "#2E7D32", // Darker green border
    ...(Platform.OS === "ios" && {
      //shadowColor: "#2E7D32",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.2,
      shadowRadius: 2,
    }),
  },
  trainingButtonText: {
    fontSize: Platform.OS === "ios" ? 12 : 14,
    textAlign: "center",
    fontWeight: "700",
    ...(Platform.OS === "ios" && {
      fontWeight: "700",
      paddingVertical: 2,
    }),
  },
  completedTrainingButtonText: {
    color: "#ffffff",
  },
  loaderContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    marginTop: 20,
    marginBottom: 20,
  },
  loaderText: {
    marginTop: 10,
    fontSize: 16,
    color: "#002366",
    fontWeight: "500",
  },
  errorContainer: {
    margin: 20,
    padding: 15,
    backgroundColor: "#fff8f8",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#ffdddd",
  },
  errorText: {
    color: "#dc3545",
    fontSize: 16,
    textAlign: "center",
  },
  undertakingContainer: {
    margin: 20,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
    color: "#002366",
  },
  undertakingItem: {
    marginBottom: 15,
    padding: 10,
    backgroundColor: "#ffffff",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#e9ecef",
  },
  undertakingHeader: {
    marginBottom: 10,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  undertakingTitle: {
    fontWeight: "bold",
    fontSize: 16,
    color: "#002366",
  },
  undertakingSubtitle: {
    fontSize: 14,
    color: "#6c757d",
  },
  undertakingDetails: {
    paddingLeft: 10,
  },
  undertakingDetail: {
    marginBottom: 5,
    fontSize: 14,
    color: "#212529",
  },
  // Header styles for breadcrumb and title
  headerContainer: {
    width: "100%",
    //paddingHorizontal: Dimensions.get("window").width < 600 ? 16 : 40,
    paddingBottom: Dimensions.get("window").width < 600 ? 12 : 16,
    backgroundColor: "transparent",
  },
  mobileHeader: {
    flex: 1,
  },
  desktopHeader: {
    flex: 1,
  },
  mobileBackRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  backIcon: {
    fontSize: 22,
    color: "#005CBC",
    fontWeight: "bold",
    marginRight: 6,
  },
  backButtonText: {
    fontSize: 16,
    color: "#005CBC",
    fontWeight: "600",
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: Dimensions.get("window").width < 600 ? 18 : 28,
    fontWeight: "700",
    color: "#2D2D2D",
    lineHeight: 40,
  },
});

export default EmployeeProfile;
